import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import styles from './PictureDaySignup.module.scss';

// "Tell me when my school's photos are ready", taken BEFORE picture day.
//
// ONE component, used by the entry screen and the registration page. The two
// screens ask for different things around it -- the entry screen knows only the
// school, the registration page already has the child's name -- so what varies
// is passed in and the signup itself is written once. Two copies of a form that
// posts to the same endpoint is how the two screens start disagreeing about
// what a signup means.
//
// It asks for ONE contact and a channel, because the server does: a phone and
// an email travelling together allowed (channel: 'email', phone: '209...'),
// a row nothing could ever send to.
const STRINGS = {
  en: {
    prompt: 'Get notified by',
    text: 'Text',
    email: 'Email',
    phonePlaceholder: '(209) 555-0147',
    emailPlaceholder: 'you@example.com',
    submit: 'Notify me',
    submitting: 'Signing you up…',
    // Named per channel: promising SMS to someone who chose email is a promise
    // about a message that will never arrive.
    consentText: 'By entering your number you agree to receive a text when photos are ready. Msg & data rates may apply.',
    consentEmail: 'By entering your address you agree to receive an email when photos are ready.',
    done: 'You’re on the list. We’ll let you know as soon as photos are ready.',
    // Alex's rule reaching the parent: this school has two children by that
    // name, so we ask rather than pick. Deliberately not phrased as an error --
    // they are already signed up, and this only sharpens what we can send.
    ambiguous: 'More than one student at this school has that name. Add their student ID and we’ll send you straight to their photos.',
    studentIdLabel: 'Student ID',
    studentIdSubmit: 'Save',
    failed: 'That didn’t go through. Please check what you entered and try again.',
  },
  es: {
    prompt: 'Recibir aviso por',
    text: 'Mensaje',
    email: 'Correo',
    phonePlaceholder: '(209) 555-0147',
    emailPlaceholder: 'usted@ejemplo.com',
    submit: 'Avísenme',
    submitting: 'Registrando…',
    consentText: 'Al ingresar su número acepta recibir un mensaje de texto cuando las fotos estén listas. Pueden aplicar tarifas de mensajes y datos.',
    consentEmail: 'Al ingresar su correo acepta recibir un aviso cuando las fotos estén listas.',
    done: 'Ya está en la lista. Le avisaremos en cuanto las fotos estén listas.',
    ambiguous: 'Más de un estudiante en esta escuela tiene ese nombre. Agregue su número de estudiante y lo enviaremos directamente a sus fotos.',
    studentIdLabel: 'Número de estudiante',
    studentIdSubmit: 'Guardar',
    failed: 'No se pudo completar. Revise los datos e intente de nuevo.',
  },
};

const TEXT = 'text';
const EMAIL = 'email';

const PictureDaySignup = ({
  schoolId, lang, student, onSignedUp, confirmInline,
}) => {
  const t = STRINGS[lang] || STRINGS.en;

  const [channel, setChannel] = useState(TEXT);
  const [contact, setContact] = useState('');
  const [studentId, setStudentId] = useState('');
  const [state, setState] = useState('idle');
  const [needsStudentId, setNeedsStudentId] = useState(false);
  const [followUpFailed, setFollowUpFailed] = useState(false);

  // `baseURL: '/'` because the customer app sets a global axios baseURL of
  // /api/v1 and this route is not under it -- without this the POST goes to
  // /api/v1/picture_day_signups and 404s. The old registration page carried the
  // same override for the same reason. A mocked axios in the unit tests cannot
  // see this, which is why it is asserted there explicitly.
  const post = (extra = {}) => axios.post('/picture_day_signups', {
    school_id: schoolId,
    channel,
    contact,
    ...student,
    ...extra,
  }, { baseURL: '/' });

  const submit = (event) => {
    if (event) event.preventDefault();
    if (!contact.trim() || state === 'saving') return;

    setState('saving');
    post()
      .then(({ data }) => {
        setNeedsStudentId(Boolean(data.needsStudentId));
        setState('done');
        if (onSignedUp) onSignedUp(data);
      })
      // The signup is the promise; a failure here means we did NOT take it, so
      // the parent has to be told rather than shown a success they did not get.
      .catch(() => setState('failed'));
  };

  const submitStudentId = (event) => {
    if (event) event.preventDefault();
    if (!studentId.trim()) return;

    setFollowUpFailed(false);
    post({ student_id: studentId })
      .then(({ data }) => {
        setNeedsStudentId(Boolean(data.needsStudentId));
        // Hands the screen back once the id has settled it, which is what lets
        // the entry form finally show its thank-you. Without this the parent
        // answers the question and nothing visibly happens.
        if (!data.needsStudentId && onSignedUp) onSignedUp(data);
      })
      // NOT swallowed. The signup itself is already safe, but a parent who
      // typed an id and saw no response cannot tell whether we got it.
      .catch(() => setFollowUpFailed(true));
  };

  // `confirmInline: false` hands the confirmation to the screen above, which
  // on the entry form replaces the whole card with a thank-you rather than
  // tucking a line under a form the parent has already finished with. The
  // registration page keeps the inline version, because the form around it
  // stays useful.
  // `|| needsStudentId` so the which-child question survives on a screen that
  // owns its own confirmation. The entry form replaces this card with a
  // thank-you, and that made the follow-up unreachable exactly where the
  // ambiguity is most likely -- a parent who has typed nothing but a name.
  if (state === 'done' && (confirmInline || needsStudentId)) {
    return (
      <div className={styles.done} role="status">
        <p className={styles.doneMessage}>{t.done}</p>

        {needsStudentId && (
          <div className={styles.followUp}>
            <p className={styles.ambiguous}>{t.ambiguous}</p>
            <label htmlFor="signup-student-id">{t.studentIdLabel}</label>
            <div className={styles.followUpRow}>
              <input
                id="signup-student-id"
                type="text"
                value={studentId}
                onChange={(event) => setStudentId(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') submitStudentId(event); }}
              />
              <button type="button" className={styles.secondaryButton} onClick={submitStudentId}>
                {t.studentIdSubmit}
              </button>
            </div>
            {followUpFailed && <p className={styles.failed}>{t.failed}</p>}
          </div>
        )}
      </div>
    );
  }

  // A DIV, NOT A FORM. This component is dropped inside the entry screen's
  // own <form>, and a nested form is invalid HTML -- React warns and the
  // browser flattens it, which is exactly what stopped the pre-picture-day
  // screen rendering at all. A shared component cannot know whether its host is
  // already a form, so it renders none and wires Enter itself.
  const submitOnEnter = (event) => {
    if (event.key === 'Enter') submit(event);
  };

  return (
    <div className={styles.signup}>
      <p className={styles.prompt}>{t.prompt}</p>

      {/* A pair of buttons rather than a select: two options that fit side by
          side read faster than a dropdown that has to be opened to see them. */}
      <div className={styles.channels} role="group" aria-label={t.prompt}>
        {[[TEXT, t.text], [EMAIL, t.email]].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`${styles.channel} ${channel === value ? styles.channelActive : ''}`}
            aria-pressed={channel === value}
            onClick={() => setChannel(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <label className={styles.srOnly} htmlFor="signup-contact">
        {channel === TEXT ? t.text : t.email}
      </label>
      <input
        id="signup-contact"
        className={styles.contact}
        // `tel` and `email` bring up the right keyboard on a phone, which is
        // where most of these are typed.
        type={channel === TEXT ? 'tel' : 'email'}
        inputMode={channel === TEXT ? 'tel' : 'email'}
        autoComplete={channel === TEXT ? 'tel' : 'email'}
        placeholder={channel === TEXT ? t.phonePlaceholder : t.emailPlaceholder}
        value={contact}
        onChange={(event) => setContact(event.target.value)}
        onKeyDown={submitOnEnter}
      />

      <button type="button" className={styles.submit} onClick={submit} disabled={state === 'saving'}>
        {state === 'saving' ? t.submitting : t.submit}
      </button>

      <p className={styles.consent}>{channel === TEXT ? t.consentText : t.consentEmail}</p>

      {state === 'failed' && (
        <p className={styles.failed} role="alert">{t.failed}</p>
      )}
    </div>
  );
};

PictureDaySignup.propTypes = {
  confirmInline: PropTypes.bool,
  schoolId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lang: PropTypes.string,
  // Optional hints. The registration page has them; the entry screen does not,
  // and the signup must work either way.
  student: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    grade: PropTypes.string,
    student_id: PropTypes.string,
  }),
  onSignedUp: PropTypes.func,
};

PictureDaySignup.defaultProps = {
  confirmInline: true,
  lang: 'en',
  student: {},
  onSignedUp: null,
};

export default PictureDaySignup;
